import React, { useState, useEffect, useRef } from 'react';
import { Search, Users, MessageSquare, User, Clock, ChevronDown, ChevronUp, ArrowLeft, Image, Video, File, MapPin, Smile, X } from 'lucide-react';
import { getToken } from '../api/auth';
import { API_BASE_URL } from '../config';

export interface WhatsAppContact {
    jid: string;
    name: string | null;
    phoneNumber: string;
    isGroup: boolean;
}

export interface WhatsAppChat {
    jid: string;
    name: string | null;
    phoneNumber: string;
    lastMessage: string | null;
    lastMessageTime: number | null;
    unreadCount: number;
    isGroup: boolean;
}

export interface WhatsAppMessage {
    id: string;
    jid: string;
    fromMe: boolean;
    senderJid: string | null;
    senderName: string | null;
    timestamp: number;
    type: 'text' | 'image' | 'video' | 'audio' | 'document' | 'sticker' | 'contact' | 'location' | 'reaction' | 'other';
    text: string | null;
    caption: string | null;
    mediaUrl: string | null;
    quotedMessageId: string | null;
}

interface WhatsAppContactsProps {
    contacts: WhatsAppContact[];
    chats: WhatsAppChat[];
    messageCounts?: { [key: string]: number };
    onSelectNumber?: (phoneNumber: string) => void;
}

export function WhatsAppContacts({ contacts, chats, messageCounts = {}, onSelectNumber }: WhatsAppContactsProps) {
    const [activeTab, setActiveTab] = useState<'chats' | 'contacts'>('chats');
    const [searchQuery, setSearchQuery] = useState('');
    const [isExpanded, setIsExpanded] = useState(true);
    const [selectedChat, setSelectedChat] = useState<WhatsAppChat | null>(null);
    const [messages, setMessages] = useState<WhatsAppMessage[]>([]);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Fetch messages when a chat is selected
    useEffect(() => {
        if (selectedChat) {
            fetchMessages(selectedChat.jid);
        }
    }, [selectedChat]);

    // Scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const fetchMessages = async (jid: string) => {
        setLoadingMessages(true);
        try {
            const token = getToken();
            const response = await fetch(`${API_BASE_URL}/whatsapp/messages/${encodeURIComponent(jid)}?limit=100`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setMessages(data.messages);
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
        } finally {
            setLoadingMessages(false);
        }
    };

    // Format message timestamp
    const formatMessageTime = (timestamp: number) => {
        const date = new Date(timestamp);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const formatMessageDate = (timestamp: number) => {
        const date = new Date(timestamp);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        
        if (date.toDateString() === today.toDateString()) {
            return 'Today';
        } else if (date.toDateString() === yesterday.toDateString()) {
            return 'Yesterday';
        } else {
            return date.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
        }
    };

    // Group messages by date
    const groupMessagesByDate = (messages: WhatsAppMessage[]) => {
        const groups: { date: string; messages: WhatsAppMessage[] }[] = [];
        let currentDate = '';
        
        for (const msg of messages) {
            const msgDate = formatMessageDate(msg.timestamp);
            if (msgDate !== currentDate) {
                currentDate = msgDate;
                groups.push({ date: msgDate, messages: [msg] });
            } else {
                groups[groups.length - 1].messages.push(msg);
            }
        }
        
        return groups;
    };

    // Get message type icon
    const getMessageTypeIcon = (type: WhatsAppMessage['type']) => {
        switch (type) {
            case 'image': return <Image className="w-4 h-4" />;
            case 'video': return <Video className="w-4 h-4" />;
            case 'document': return <File className="w-4 h-4" />;
            case 'location': return <MapPin className="w-4 h-4" />;
            case 'sticker': return <Smile className="w-4 h-4" />;
            default: return null;
        }
    };

    // Filter contacts based on search query
    const filteredContacts = contacts.filter(contact => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            (contact.name?.toLowerCase().includes(query)) ||
            contact.phoneNumber.includes(query)
        );
    }).filter(c => !c.isGroup); // Exclude groups from contacts

    // Filter chats based on search query
    const filteredChats = chats.filter(chat => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            (chat.name?.toLowerCase().includes(query)) ||
            chat.phoneNumber.includes(query) ||
            (chat.lastMessage?.toLowerCase().includes(query))
        );
    });

    // Format timestamp
    const formatTime = (timestamp: number | null) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        const now = new Date();
        const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
        
        if (diffDays === 0) {
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } else if (diffDays === 1) {
            return 'Yesterday';
        } else if (diffDays < 7) {
            return date.toLocaleDateString([], { weekday: 'short' });
        } else {
            return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
        }
    };

    const handleSelect = (phoneNumber: string) => {
        if (onSelectNumber && phoneNumber) {
            onSelectNumber(phoneNumber);
        }
    };

    return (
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 overflow-hidden">
            {/* Header */}
            <div 
                className="p-4 border-b border-white/10 flex items-center justify-between cursor-pointer hover:bg-white/5 transition-colors"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center">
                        <MessageSquare className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-white">WhatsApp Contacts</h3>
                        <p className="text-sm text-gray-400">
                            {chats.length} chats • {contacts.filter(c => !c.isGroup).length} contacts
                        </p>
                    </div>
                </div>
                {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
            </div>

            {isExpanded && (
                <>
                    {/* Tabs */}
                    <div className="flex border-b border-white/10">
                        <button
                            onClick={() => setActiveTab('chats')}
                            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                                activeTab === 'chats'
                                    ? 'text-green-400 border-b-2 border-green-400 bg-green-400/10'
                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                            }`}
                        >
                            <MessageSquare className="w-4 h-4 inline mr-2" />
                            Chats ({filteredChats.length})
                        </button>
                        <button
                            onClick={() => setActiveTab('contacts')}
                            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                                activeTab === 'contacts'
                                    ? 'text-green-400 border-b-2 border-green-400 bg-green-400/10'
                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                            }`}
                        >
                            <Users className="w-4 h-4 inline mr-2" />
                            Contacts ({filteredContacts.length})
                        </button>
                    </div>

                    {/* Search */}
                    <div className="p-4 border-b border-white/10">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search contacts or chats..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500/50"
                            />
                        </div>
                    </div>

                    {/* Content */}
                    <div className="max-h-96 overflow-y-auto">
                        {activeTab === 'chats' ? (
                            filteredChats.length > 0 ? (
                                <div className="divide-y divide-white/5">
                                    {filteredChats.map((chat) => (
                                        <div
                                            key={chat.jid}
                                            className="p-4 hover:bg-white/5 cursor-pointer transition-colors"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                                                    chat.isGroup 
                                                        ? 'bg-gradient-to-br from-purple-400 to-purple-600' 
                                                        : 'bg-gradient-to-br from-gray-400 to-gray-600'
                                                }`}>
                                                    {chat.isGroup ? (
                                                        <Users className="w-6 h-6 text-white" />
                                                    ) : (
                                                        <User className="w-6 h-6 text-white" />
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0" onClick={() => handleSelect(chat.phoneNumber)}>
                                                    <div className="flex items-center justify-between">
                                                        <span className="font-medium text-white truncate">
                                                            {chat.name || chat.phoneNumber}
                                                        </span>
                                                        <div className="flex items-center gap-2">
                                                            {messageCounts[chat.jid] && (
                                                                <span className="text-xs text-gray-500">
                                                                    {messageCounts[chat.jid]} msgs
                                                                </span>
                                                            )}
                                                            {chat.lastMessageTime && (
                                                                <span className="text-xs text-gray-500 flex items-center gap-1">
                                                                    <Clock className="w-3 h-3" />
                                                                    {formatTime(chat.lastMessageTime)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center justify-between mt-1">
                                                        <p className="text-sm text-gray-400 truncate">
                                                            {chat.lastMessage || (chat.isGroup ? 'Group chat' : chat.phoneNumber)}
                                                        </p>
                                                        {chat.unreadCount > 0 && (
                                                            <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full font-medium">
                                                                {chat.unreadCount}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedChat(chat);
                                                    }}
                                                    className="px-3 py-1.5 text-xs bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-colors"
                                                >
                                                    View
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="p-8 text-center text-gray-400">
                                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                    <p>No chats found</p>
                                    <p className="text-sm mt-1">Chats will appear after WhatsApp syncs</p>
                                </div>
                            )
                        ) : (
                            filteredContacts.length > 0 ? (
                                <div className="divide-y divide-white/5">
                                    {filteredContacts.map((contact) => (
                                        <div
                                            key={contact.jid}
                                            onClick={() => handleSelect(contact.phoneNumber)}
                                            className="p-4 hover:bg-white/5 cursor-pointer transition-colors"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center">
                                                    <User className="w-5 h-5 text-white" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <span className="font-medium text-white truncate block">
                                                        {contact.name || 'Unknown'}
                                                    </span>
                                                    <span className="text-sm text-gray-400">
                                                        +{contact.phoneNumber}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="p-8 text-center text-gray-400">
                                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                    <p>No contacts found</p>
                                    <p className="text-sm mt-1">Contacts will appear after WhatsApp syncs</p>
                                </div>
                            )
                        )}
                    </div>
                </>
            )}

            {/* Message Viewer Modal */}
            {selectedChat && (
                <div 
                    className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setSelectedChat(null);
                    }}
                >
                    <div className="bg-slate-900 rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col border border-white/20 shadow-2xl relative">
                        {/* Close button - top right corner, always visible */}
                        <button
                            onClick={() => setSelectedChat(null)}
                            className="absolute -top-3 -right-3 w-10 h-10 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white shadow-lg transition-colors z-10"
                            title="Close"
                        >
                            <X className="w-6 h-6" />
                        </button>
                        
                        {/* Modal Header */}
                        <div className="p-4 border-b border-white/10 flex items-center gap-3">
                            <button
                                onClick={() => setSelectedChat(null)}
                                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5 text-gray-400" />
                            </button>
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                selectedChat.isGroup 
                                    ? 'bg-gradient-to-br from-purple-400 to-purple-600' 
                                    : 'bg-gradient-to-br from-green-400 to-green-600'
                            }`}>
                                {selectedChat.isGroup ? (
                                    <Users className="w-5 h-5 text-white" />
                                ) : (
                                    <User className="w-5 h-5 text-white" />
                                )}
                            </div>
                            <div className="flex-1">
                                <h3 className="font-semibold text-white">
                                    {selectedChat.name || selectedChat.phoneNumber}
                                </h3>
                                <p className="text-sm text-gray-400">
                                    {messages.length} messages loaded
                                </p>
                            </div>
                            <button
                                onClick={() => handleSelect(selectedChat.phoneNumber)}
                                className="px-3 py-1.5 text-sm bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg transition-colors"
                            >
                                Track
                            </button>
                        </div>

                        {/* Messages Area */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-950/50">
                            {loadingMessages ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="animate-spin w-8 h-8 border-2 border-green-400 border-t-transparent rounded-full" />
                                </div>
                            ) : messages.length === 0 ? (
                                <div className="text-center py-12 text-gray-400">
                                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                    <p>No messages found</p>
                                </div>
                            ) : (
                                groupMessagesByDate(messages).map((group, groupIndex) => (
                                    <div key={groupIndex}>
                                        {/* Date Separator */}
                                        <div className="flex items-center justify-center my-4">
                                            <span className="px-3 py-1 bg-white/10 rounded-full text-xs text-gray-400">
                                                {group.date}
                                            </span>
                                        </div>
                                        
                                        {/* Messages */}
                                        {group.messages.map((msg) => (
                                            <div
                                                key={msg.id}
                                                className={`flex ${msg.fromMe ? 'justify-end' : 'justify-start'} mb-2`}
                                            >
                                                <div
                                                    className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                                                        msg.fromMe
                                                            ? 'bg-green-600 text-white rounded-br-none'
                                                            : 'bg-white/10 text-white rounded-bl-none'
                                                    }`}
                                                >
                                                    {/* Sender name for groups */}
                                                    {selectedChat.isGroup && !msg.fromMe && msg.senderName && (
                                                        <p className="text-xs text-green-300 font-medium mb-1">
                                                            {msg.senderName}
                                                        </p>
                                                    )}
                                                    
                                                    {/* Message type indicator */}
                                                    {msg.type !== 'text' && (
                                                        <div className="flex items-center gap-1 text-gray-300 mb-1">
                                                            {getMessageTypeIcon(msg.type)}
                                                            <span className="text-xs capitalize">{msg.type}</span>
                                                        </div>
                                                    )}
                                                    
                                                    {/* Message content */}
                                                    <p className="text-sm whitespace-pre-wrap break-words">
                                                        {msg.text || msg.caption || '(No content)'}
                                                    </p>
                                                    
                                                    {/* Timestamp */}
                                                    <p className={`text-xs mt-1 text-right ${
                                                        msg.fromMe ? 'text-green-200' : 'text-gray-500'
                                                    }`}>
                                                        {formatMessageTime(msg.timestamp)}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ))
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Footer note */}
                        <div className="p-3 border-t border-white/10 text-center">
                            <p className="text-xs text-gray-500">
                                Messages are read-only. This is a tracking tool, not a messaging client.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
